import { Schema, SchemaGetter } from "effect"
import { AllowRule } from "./allow"
import {
  AgentId,
  AreaId,
  ArtifactId,
  CommentId,
  EdgeId,
  GoalId,
  ItemId,
  KpiId,
  MemoryItemId,
  Millis,
  PolicyId,
  ProjectId,
  ProposalId,
  RepoId,
  RunId,
  RunnerId,
  Seq,
  UserId,
  WorkspaceId,
} from "./ids"

// #region log refs
/** Each state-owning DO has its own log; a seq is only meaningful together with its log. */
export const LogId = Schema.TaggedUnion({
  project: { projectId: ProjectId },
  workspace: { workspaceId: WorkspaceId },
  agent: { agentId: AgentId },
  user: { userId: UserId },
})
export const EventRef = Schema.Struct({ log: LogId, seq: Seq })
export type EventRef = typeof EventRef.Type
// #endregion log refs

// #region forward compatibility
/**
 * A closed set of string literals that decodes any other string as `fallback` (encoding stays strict). For fields a
 * runner decodes but never acts on (incident 2026-10-15: runner 0.9.4 could not decode a `turn.start` carrying 0084's
 * new `LoadedDocument.reason` values and stalled at "Preparing workspace"). Never use it where the value changes
 * behaviour: an unknown permission mode or effort must still fail loudly.
 */
export const tolerantLiterals = <const L extends readonly [string, ...Array<string>]>(
  literals: L,
  fallback: L[number],
) =>
  Schema.Union([
    Schema.Literals(literals),
    Schema.String.pipe(
      Schema.decodeTo(Schema.toType(Schema.Literals(literals)), {
        decode: SchemaGetter.transform((): L[number] => fallback),
        encode: SchemaGetter.forbidden(() => "an unknown literal is never encoded"),
      }),
    ),
  ])
// #endregion forward compatibility

// #region actors
export const Visibility = Schema.Literals(["workspace", "private"])
export const Taint = Schema.Literals(["clean", "tainted"])
export type Taint = typeof Taint.Type
/**
 * The CLI family a runner profile runs. `antigravity` (decision 0109): Google's Antigravity CLI (`agy`), local runners
 * only (never the Platform Runner), advertised only to a cloud whose Register reply carries `agent_antigravity`.
 * `muse` (decision 0110): Meta's Muse Code (`muse serve`), local runners only, advertised only to a cloud with
 * `agent_muse`.
 */
export const AgentKind = Schema.Literals(["claude", "codex", "antigravity", "muse"])
export type AgentKind = typeof AgentKind.Type
/**
 * Decision 0118: the CLIs automatic placement may choose for work that names none. Antigravity and Muse Code are opt-in:
 * they run only where a routing rule or choice, a worker preset, the item's hands, an agent's mind, a retry route or a
 * profile pin names them.
 */
export const AUTO_PLACED_AGENTS: ReadonlyArray<AgentKind> = ["claude", "codex"]
/** The CLI a runner agent kind runs, as people name it. */
export const AGENT_LABEL: Readonly<Record<AgentKind, string>> = {
  claude: "Claude Code",
  codex: "Codex",
  antigravity: "Antigravity",
  muse: "Muse Code",
}
export const ModelProvider = Schema.Literals(["anthropic", "openai"])
/**
 * Who a usage row's tokens and a plan belong to: a mind's `ModelProvider`, or `google` (decision 0109) for a run on an
 * Antigravity profile, whatever model it picked (the Google login pays), or `meta` (decision 0110) for a Muse Code
 * run. Only from a cloud with `agent_antigravity` / `agent_muse`.
 */
export const UsageProvider = Schema.Literals(["anthropic", "openai", "google", "meta"])
export type UsageProvider = typeof UsageProvider.Type
export const Effort = Schema.Literals(["low", "medium", "high", "max"])
export type Effort = typeof Effort.Type
/** A model and effort for one CLI family; null = the profile's override, else the CLI default (settings wave §5.1). */
export const ModelChoice = Schema.Struct({
  model: Schema.NullOr(Schema.String.check(Schema.isMaxLength(128))),
  effort: Schema.NullOr(Effort),
})
export type ModelChoice = typeof ModelChoice.Type
/** Model and effort per CLI family, for a run whose family the lease decides (`Run.byAgent`, `JobTemplate.byAgent`). */
export const ModelsByAgent = Schema.Struct({
  claude: Schema.optionalKey(ModelChoice),
  codex: Schema.optionalKey(ModelChoice),
  /** Decision 0109; an older runner's decoder ignores the key (excess keys are dropped). */
  antigravity: Schema.optionalKey(ModelChoice),
  muse: Schema.optionalKey(ModelChoice),
})
export type ModelsByAgent = typeof ModelsByAgent.Type

/** Platform processes. None of them can issue an item- or goal-creating command (structural, §8.1). */
export const SystemProcess = Schema.Literals([
  "scheduler", //    dispatches runs for ready items, notes overdue items; never creates items
  "lease_reaper", // ends a started run whose lease expired (runner_lost)
  "watchdog", //     stall and loop detection; escalates only (question proposal)
  "expirer", //      proposal expiry (denied, or the question's default option)
  "membership", //   mirrors Better Auth membership and the Mayor record into project DOs
  "kpi_sync", //     records KPI samples from connectors (Phase 3)
  "gateway", //      reports outward executions (Phase 2)
  "upgrade", //      decision 0032's policy backfill (read-only since decision 0036: kept so old events decode)
  "github_app", //   decision 0069: relays GitHub App webhooks and the hourly reconcile as RecordPrState; never creates items
  "pages", //        decision 0084: the ensure_pages bootstrap/migration job; creates project pages (documents) only
  "people", //       decision 0086: writes workspace people pages' account frontmatter (create, move, archive, restore)
])

/**
 * Who did something. Never sent by a client: stamped from the authenticated credential (session, runner token,
 * per-run MCP token, internal DO identity). `onBehalfOf` is the agent's owner, copied from the roster by the server.
 */
export const Actor = Schema.TaggedUnion({
  user: { userId: UserId },
  /** A persistent agent (lead, role agent or Mayor) inside one of its turn runs, on any mind. */
  agent: { agentId: AgentId, runId: RunId, onBehalfOf: UserId },
  /** A temporary run with no persistent identity: worker or validator. `agentId` is the item's owner agent. */
  worker: { runId: RunId, itemId: ItemId, agentId: AgentId, onBehalfOf: UserId },
  /** Run lifecycle facts relayed by the runner hub (lease, start, end, milestones). Never graph edits. */
  runner: { runnerId: RunnerId },
  system: { process: SystemProcess },
})
export type Actor = typeof Actor.Type
// #endregion actors

// #region meta
/** On every Workspace-level record. `ownerId` is always a human; steer rights and approval routing key off it. */
export const Owned = { ownerId: UserId, visibility: Visibility } as const

/** On every Project-level record (graph, ledger, memory, runs). Attribution is separate from ownership. */
export const RecordMeta = {
  ...Owned,
  createdBy: Actor,
  createdAt: Millis,
  updatedBy: Actor,
  updatedAt: Millis,
  /** Seq of the last event that touched this record. Commands name it in `expect` for optimistic concurrency. */
  lastSeq: Seq,
} as const

/**
 * On records whose free text is written whole (memory items, artifacts, comments, proposals): the seq of the event
 * that last wrote that content. Status changes move `lastSeq`, never this, so `Redacted` still finds the record.
 */
export const ContentMeta = { contentSeq: Seq } as const

/** Pin: agents can't move it (rank, parent, owner, assignee). Lock: agents can't edit it at all (they may comment or propose). */
export const Hold = Schema.TaggedUnion({
  none: {},
  pinned: { by: UserId, at: Millis, note: Schema.NullOr(Schema.String) },
  locked: { by: UserId, at: Millis, note: Schema.NullOr(Schema.String) },
})

/** Graph nodes (what the canvas draws and selection carries across views). */
export const NodeRef = Schema.TaggedUnion({
  goal: { id: GoalId },
  kpi: { id: KpiId },
  item: { id: ItemId },
  area: { id: AreaId },
  agent: { id: AgentId },
})
export type NodeRef = typeof NodeRef.Type

/** Anything addressable in a project: nodes plus edges, ledger items, memory, artifacts, comments, runs, policies. */
export const EntityRef = Schema.TaggedUnion({
  goal: { id: GoalId },
  kpi: { id: KpiId },
  item: { id: ItemId },
  area: { id: AreaId },
  agent: { id: AgentId },
  edge: { id: EdgeId },
  proposal: { id: ProposalId },
  memory: { id: MemoryItemId },
  artifact: { id: ArtifactId },
  comment: { id: CommentId },
  run: { id: RunId },
  policy: { id: PolicyId },
  /** Decision 0142: a conversation (`T-n`); a direct thread's messages target it. */
  thread: { id: CommentId },
})
export type EntityRef = typeof EntityRef.Type

/** Work nodes: the only endpoints of `blocks` / `informs` edges. Items (and goals) carry a rank among siblings. */
export const WorkRef = Schema.TaggedUnion({ item: { id: ItemId } })
export const RankedRef = Schema.TaggedUnion({
  goal: { id: GoalId },
  item: { id: ItemId },
})

/** A mention target or reviewer. People and agents share one @handle namespace per project. */
export const Principal = Schema.TaggedUnion({ user: { userId: UserId }, agent: { agentId: AgentId } })

export type Principal = typeof Principal.Type

/** An `update` comment's health (decision 0035): on a goal or an item; the latest one is its health. */
export const Health = Schema.Literals(["on_track", "at_risk", "off_track"])
export type Health = typeof Health.Type

export const Risk = Schema.Literals(["low", "medium", "high"])
export const Reversibility = Schema.Literals(["reversible", "costly", "irreversible"])
/** Attention tiers: blocking pushes now, soon goes to Needs-you (batched push), fyi to the digest, ambient to the canvas. */
export const Tier = Schema.Literals(["blocking", "soon", "fyi", "ambient"])

/**
 * Codex's own "remember" choices a relayed command prompt offers (runner 0.6.1, decision 0053). Absent = Allow / Deny
 * only. Carried on `permission.request` and on the `permission` proposal body.
 */
export const PermissionOptions = Schema.Struct({
  /** "Allow for this run": Codex `acceptForSession` (in-memory, this thread, exact command). */
  forRun: Schema.optionalKey(Schema.Boolean),
  /**
   * Codex's own proposed exec-policy prefix, for display only: "Always allow `pnpm check`". The runner answers with the
   * prefix it holds, never a value from the cloud. Present only when Codex proposed one.
   */
  alwaysPrefix: Schema.optionalKey(Schema.Array(Schema.String)),
  /**
   * Wave O1 (RF2): the tool's approval is the interaction (Claude `requiresUserInteraction`): the runner answers every
   * allow as Allow once, so the card offers Allow once / Deny only (no "Allow for this run").
   */
  onceOnly: Schema.optionalKey(Schema.Boolean),
  /**
   * Decision 0096: the allowlist rules "Always allow" adds (project level), from the CLI's own proposal: Codex's prefix,
   * the write paths of a permissions request or a file change's root, Claude's `canUseTool` suggestions. "Always allow"
   * answers the CLI for this run (session scope) and adds these rules for later runs. Present only from a runner with
   * `access_allow`.
   */
  allow: Schema.optionalKey(Schema.Array(AllowRule).check(Schema.isMaxLength(10))),
  /**
   * Decision 0105: the CLI's own automatic reviewer refused this action (Codex's auto-review, Claude Code's auto mode
   * classifier). The CLI already said no; the card asks whether a person allows it anyway (Allow once / Always allow /
   * Deny) and expires after `REVIEW_CARD_DEADLINE_MS` (then it stays refused). `reason` is the reviewer's own words.
   * Present only from a runner that surfaces review denials (0.9.9).
   */
  review: Schema.optionalKey(
    Schema.Struct({
      cli: Schema.Literals(["codex", "claude"]),
      reason: Schema.optionalKey(Schema.String.check(Schema.isMaxLength(500))),
      /** The reviewer's risk label as it said it (`low` … `critical`). */
      risk: Schema.optionalKey(Schema.String.check(Schema.isMaxLength(20))),
    }),
  ),
})
export type PermissionOptions = typeof PermissionOptions.Type

/**
 * Decision 0105: how long a review-denial card waits for a person (the design's default, 30 minutes). On expiry the
 * CLI keeps its own refusal and the run goes on as it would have; nothing is ever allowed by a timeout.
 */
export const REVIEW_CARD_DEADLINE_MS = 30 * 60_000

/** Why a claim holds. Shared by run outcomes, validator verdicts, memory items and bet verdicts. */
export const Evidence = Schema.TaggedUnion({
  /** Native tool result, deployment id, message id, merged SHA or live publication reference. */
  execution: {
    action: Schema.String.check(Schema.isMinLength(1), Schema.isMaxLength(500)),
    resultRef: Schema.String.check(Schema.isMinLength(1), Schema.isMaxLength(2_000)),
    proposalId: Schema.optionalKey(ProposalId),
    revision: Schema.optionalKey(Schema.Int.check(Schema.isGreaterThan(0))),
  },
  artifact: { artifactId: ArtifactId },
  check: { name: Schema.String, passed: Schema.Boolean, detail: Schema.NullOr(Schema.String) },
  pull_request: { url: Schema.String, headSha: Schema.String },
  commit: { repoId: RepoId, sha: Schema.String },
  url: { url: Schema.String, note: Schema.String }, // tainted by definition
  run: { runId: RunId },
  kpi: { kpiId: KpiId, from: Millis, to: Millis },
  statement: { userId: UserId, at: Millis }, // "remember that…"
})
// #endregion meta
